import { Controller } from "@hotwired/stimulus";
import "./component.scss";

// Switching the shared product and copying its link (PLAN.md, Slice 8).
//
// No URL is ever built here: the server rendered one complete set per product
// into `targetsValue`, and this controller only picks one of them. Same for the
// copy feedback — both messages come from the server, so the sheet has no
// hardcoded copy in JavaScript.
export default class extends Controller {
  static targets = ["select", "url", "copy", "status", "channel"];

  static values = {
    targets: Object,
    copiedText: String,
    failedText: String,
    titleId: String,
  };

  connect() {
    this.statusTimeout = null;
    this.nameDialog();
  }

  disconnect() {
    if (this.statusTimeout) {
      window.clearTimeout(this.statusTimeout);
      this.statusTimeout = null;
    }
    this.unnameDialog();
  }

  // The <dialog> comes from the layout and is shared by every dialog of the app,
  // so it cannot carry a name of its own. Screen readers announce an unnamed
  // dialog as just "dialog", which says nothing about what opened.
  nameDialog() {
    const dialog = this.element.closest("dialog");
    if (!dialog || !this.titleIdValue) return;

    this.namedDialog = dialog;
    dialog.setAttribute("aria-labelledby", this.titleIdValue);
  }

  // Only our own wiring is removed: another dialog may have taken the element
  // over in the meantime, and its name must not be dropped with ours.
  unnameDialog() {
    if (!this.namedDialog) return;

    if (this.namedDialog.getAttribute("aria-labelledby") === this.titleIdValue) {
      this.namedDialog.removeAttribute("aria-labelledby");
    }
    this.namedDialog = null;
  }

  select() {
    const target = this.currentTarget();
    if (!target) return;

    this.urlTarget.value = target.url;
    this.channelTargets.forEach((link) => {
      const url = target.channels?.[link.dataset.channel];
      if (url) link.href = url;
    });

    this.clearStatus();
  }

  async copy() {
    const url = this.urlTarget.value;

    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(url);
      this.announce(this.copiedTextValue);
    } catch (_error) {
      // Failing silently would leave the affiliate believing they hold the link.
      // The message names the fallback, and the fallback is made ready: focus
      // first (without scrolling the sheet away under the user), then select the
      // whole link so a single Cmd/Ctrl+C finishes the job.
      this.announce(this.failedTextValue, true);
      this.urlTarget.focus({ preventScroll: true });
      this.urlTarget.select();
    }
  }

  async shareMore(event) {
    event.preventDefault();

    const target = this.currentTarget();
    if (!target) return;

    if (navigator.share) {
      try {
        await navigator.share({ title: target.title, text: target.text, url: target.url });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    await this.copy();
  }

  // Keyed by the typed target key ("product:12", "coaching_session:6"), not by a
  // bare id: the three target families have independent id sequences, so an id
  // alone can name two different things.
  currentTarget() {
    const key = this.hasSelectTarget ? this.selectTarget.value : Object.keys(this.targetsValue)[0];

    return this.targetsValue?.[key];
  }

  announce(message, failed = false) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("is-error", failed);

    if (this.statusTimeout) window.clearTimeout(this.statusTimeout);
    this.statusTimeout = window.setTimeout(() => this.clearStatus(), 4000);
  }

  clearStatus() {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = "";
    this.statusTarget.classList.remove("is-error");
  }
}
